// Chalk Explainer Ad's draft as the form keeps it (spec G14-chalk-explainer-ad, PR d1): the server
// holds the member's draft (`.../templates/chalk-explainer-ad/draft`), so it survives the panel;
// the form reads it, keeps its edits there, and asks the server's pack reader, directors and
// stills (live's `useChalkDraft`, which ran them from the browser). As Pixar Talking Ad's
// (`pixar-draft.ts`), with live's "Back to script" from the storyboard, and the timing plan, the
// storyboard's rows and the script's problems the server computes on each write.
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";

import { ApiError } from "../../../api/errors";
import {
  getGetChalkExplainerAdDraftQueryKey,
  useApproveChalkExplainerAdScript,
  useBackToChalkExplainerAdScript,
  useGetChalkExplainerAdDraft,
  useRegenerateChalkExplainerAdStill,
  useResetChalkExplainerAdDraft,
  useWriteChalkExplainerAdDraft,
  useWriteChalkExplainerAdScript,
  writeChalkExplainerAdDraft,
} from "../../../api/generated/endpoints/generation/generation";
import type {
  ChalkDraftStill,
  ChalkDraftStillName,
  ChalkExplainerAdDraft,
  ChalkScript,
  ChalkStills,
} from "../../../api/generated/model";
import {
  ChalkExplainerAdApproval,
  ChalkExplainerAdDraftWrite,
  ChalkExplainerAdInputs,
  ChalkExplainerAdScriptAsk,
  type ChalkExplainerAdDraftWriteOutput,
  type ChalkExplainerAdInputsOutput,
} from "../../../api/generated/zod";
import { soonest, urlRefreshIn, urlsDue } from "../../../api/signed-urls";

/** The form as it stands: its picks and its script. */
export interface ChalkForm {
  inputs: ChalkExplainerAdInputsOutput;
  script: ChalkScript | null;
}

/** Live's five stills in display order (`STILL_NAMES`). */
export const STILL_NAMES: readonly ChalkDraftStillName[] = ["board0", "end_a", "end_b", "end_c", "end_card"];

/** What each still is drawn from (live's `STILL_GRAPH.sources`): each board from the one before;
 * the blank board and the end card from no still. */
const STILL_SOURCES: Readonly<Partial<Record<ChalkDraftStillName, ChalkDraftStillName>>> = {
  end_a: "board0",
  end_b: "end_a",
  end_c: "end_b",
};

/** The video's settings live's footer keeps open at the storyboard (the subtitles and the
 * resolution): the form's own until "Generate final video" sends them. The takes are fixed at
 * Approve script, as live's. */
export interface VideoSettings {
  subtitles: boolean;
  caption_position: ChalkExplainerAdInputsOutput["caption_position"];
  caption_style_id: string;
  resolution: ChalkExplainerAdInputsOutput["resolution"];
}

/** A board still, drawn once the board director's spec is kept (live's `isBoardStill`). */
function boardStill(name: ChalkDraftStillName): boolean {
  return name !== "board0" && name !== "end_card";
}

/**
 * A still under way (live's `isStillWaiting`, and its board stills waiting on the board
 * director): drawing, or pending on a still that is done or still coming.
 */
export function stillWaiting(
  name: ChalkDraftStillName,
  stills: readonly ChalkDraftStill[],
  boarding: boolean,
): boolean {
  if (boarding && boardStill(name)) return true;
  const status = stills.find((each) => each.name === name)?.status;
  if (status === "working") return true;
  if (status !== "pending") return false;
  const source = STILL_SOURCES[name];
  if (source === undefined) return false;
  return stills.find((each) => each.name === source)?.status === "done" || stillWaiting(source, stills, boarding);
}

/** The stills the draft drew, by name, once all five are done; null before. */
export function drawnStills(draft: ChalkExplainerAdDraft): ChalkStills | null {
  if (draft.stage === "script") return null;
  const drawn = new Map(draft.stills.flatMap((each) => (each.output ? [[each.name, each.output] as const] : [])));
  const [board0, endA, endB, endC, endCard] = STILL_NAMES.map((name) => drawn.get(name));
  if (board0 === undefined || endA === undefined || endB === undefined || endC === undefined || endCard === undefined) {
    return null;
  }
  return { board0, end_a: endA, end_b: endB, end_c: endC, end_card: endCard };
}

/** "Generate final video"'s body (live's hand-off): the approved script and board, the five
 * stills by reference, the takes fixed at approval and the footer's settings; null until every
 * still is done. */
export function videoBody(draft: ChalkExplainerAdDraft, settings: VideoSettings) {
  if (draft.script === null || draft.board === null) return null;
  const stills = drawnStills(draft);
  if (stills === null) return null;
  const takes = ChalkExplainerAdInputs.safeParse(draft.inputs);
  return {
    type: "video.template" as const,
    inputs: {
      template: "chalk-explainer-ad" as const,
      script: draft.script,
      board: draft.board,
      stills,
      takes: takes.success ? takes.data.takes : 1,
      ...settings,
    },
  };
}

/** The draft refetched while it works: every 2 s while a director or a still works, and a moment
 * after (a step's end moves the draft a moment after its job ends). */
const POLL_MS = 2_000;
const SETTLE_MS = 6_000;
/** The form's edits are kept on the server once typing pauses this long. */
const SAVE_WAIT_MS = 800;

/** Where the draft's work stands, as live's progress line names it (`run-progress.ts`). */
export interface DraftWork {
  reading: boolean;
  writing: boolean;
  boarding: boolean;
  drawing: boolean;
}

export function workOf(draft: ChalkExplainerAdDraft, asked: string | null): DraftWork {
  const answering = asked === draft.updated_at && draft.stage === "script" && draft.problem === null;
  const boarding = draft.board_director === "working";
  return {
    reading: draft.pack_director === "working",
    writing: draft.script_director === "working" || (answering && draft.pack_director !== "working"),
    boarding,
    drawing: draft.stills.some((each) => stillWaiting(each.name, draft.stills, boarding)),
  };
}

function working(work: DraftWork): boolean {
  return work.reading || work.writing || work.boarding || work.drawing;
}

/** The form a draft holds, its picks with the contract's defaults. */
export function formOf(draft: ChalkExplainerAdDraft): ChalkForm {
  const inputs = ChalkExplainerAdInputs.safeParse(draft.inputs);
  return {
    inputs: inputs.success ? inputs.data : ChalkExplainerAdInputs.parse({}),
    script: draft.script,
  };
}

/** A refusal because the draft is locked (`TEMPLATE_DRAFT_LOCKED`). */
function locked(error: unknown): boolean {
  return error instanceof ApiError && error.status === 409;
}

/**
 * The member's draft and what the form does with it: the form shows the draft as read; edits in
 * the script stage, nothing working, are kept with PUT a moment after typing pauses, whose answer
 * brings the timing plan and the script's problems of the script as edited; each ask answers with
 * the draft, shown from then on.
 */
export function useChalkDraft(teamId: string, brandId: string) {
  const queryClient = useQueryClient();
  const key = getGetChalkExplainerAdDraftQueryKey(teamId, brandId);
  const [asked, setAsked] = useState<string | null>(null);
  const [local, setLocal] = useState<ChalkForm | null>(null);
  const [failure, setFailure] = useState<unknown>(null);
  const lastBusy = useRef(0);
  const read = useGetChalkExplainerAdDraft(teamId, brandId, {
    query: {
      refetchInterval: (query) => {
        const data = query.state.data;
        const now = Date.now();
        if (data !== undefined && working(workOf(data, asked))) lastBusy.current = now;
        const poll: number | false = now - lastBusy.current < SETTLE_MS ? POLL_MS : false;
        return soonest(poll, urlRefreshIn(data?.urls_expire_at, now));
      },
      refetchOnWindowFocus: (query) => (urlsDue(query.state.data?.urls_expire_at) ? "always" : true),
    },
  });
  const draft = read.data;
  const refetch = () => void queryClient.invalidateQueries({ queryKey: key });
  const took = (answer: ChalkExplainerAdDraft) => {
    queryClient.setQueryData(key, answer);
    lastBusy.current = Date.now();
    setLocal(null);
  };
  const refused = (error: unknown) => {
    setFailure(error);
    if (locked(error)) refetch();
  };
  const asks = { mutation: { onSuccess: took, onError: refused } };
  const script = useWriteChalkExplainerAdScript({
    mutation: {
      onSuccess: (answer) => {
        took(answer);
        setAsked(answer.updated_at);
      },
      onError: refused,
    },
  });
  const approval = useApproveChalkExplainerAdScript(asks);
  const back = useBackToChalkExplainerAdScript(asks);
  const regeneration = useRegenerateChalkExplainerAdStill(asks);
  const reset = useResetChalkExplainerAdDraft(asks);
  const asking = script.isPending || approval.isPending || back.isPending || regeneration.isPending || reset.isPending;
  const work: DraftWork =
    draft === undefined
      ? { reading: false, writing: false, boarding: false, drawing: false }
      : workOf(draft, asked);
  const busy = asking || working(work);
  const server = draft === undefined ? null : formOf(draft);
  const form = draft?.stage === "script" && local !== null ? local : server;
  const editable = draft?.stage === "script" && !busy;

  const save = useWriteChalkExplainerAdDraft({
    mutation: {
      onSuccess: (answer) => {
        // The edits stay the form's own; the answer brings what the server reads from them.
        queryClient.setQueryData(key, answer);
        setFailure(null);
      },
      onError: (error) => {
        if (locked(error)) {
          setLocal(null);
          refetch();
        } else {
          setFailure(error);
        }
      },
    },
  });
  const { mutate: write } = save;
  const unsaved = useRef<{ teamId: string; brandId: string; data: ChalkExplainerAdDraftWriteOutput } | null>(null);
  useEffect(
    () => () => {
      const pending = unsaved.current;
      if (pending !== null) {
        writeChalkExplainerAdDraft(pending.teamId, pending.brandId, pending.data).catch(() => undefined);
      }
    },
    [],
  );
  useEffect(() => {
    if (local === null || !editable) return undefined;
    const body = ChalkExplainerAdDraftWrite.safeParse({ inputs: local.inputs, script: local.script });
    if (!body.success) return undefined;
    const data = body.data;
    unsaved.current = { teamId, brandId, data };
    const timer = setTimeout(() => {
      unsaved.current = null;
      write({ teamId, brandId, data });
    }, SAVE_WAIT_MS);
    return () => {
      clearTimeout(timer);
      unsaved.current = null;
    };
  }, [local, editable, teamId, brandId, write]);

  const ask = () => setFailure(null);
  return {
    read,
    draft,
    form,
    work,
    busy,
    editable,
    failure,
    approving: approval.isPending,
    resetting: reset.isPending,
    edit(change: Partial<ChalkForm>) {
      if (server === null) return;
      setLocal((current) => ({ ...(current ?? server), ...change }));
    },
    editInputs(change: Partial<ChalkExplainerAdInputsOutput>) {
      if (server === null) return;
      setLocal((current) => {
        const base = current ?? server;
        return { ...base, inputs: { ...base.inputs, ...change } };
      });
    },
    generateScript() {
      if (form === null) return;
      const body = ChalkExplainerAdScriptAsk.safeParse({ inputs: form.inputs });
      if (!body.success) return;
      ask();
      script.mutate({ teamId, brandId, data: body.data });
    },
    approve() {
      if (form === null) return;
      const body = ChalkExplainerAdApproval.safeParse({ inputs: form.inputs, script: form.script });
      if (!body.success) return;
      ask();
      approval.mutate({ teamId, brandId, data: body.data });
    },
    backToScript() {
      ask();
      back.mutate({ teamId, brandId });
    },
    regenerate(name: ChalkDraftStillName) {
      ask();
      regeneration.mutate({ teamId, brandId, name });
    },
    startOver() {
      ask();
      setAsked(null);
      reset.mutate({ teamId, brandId });
    },
  };
}
